/**
 * eSign Launchpad API Client
 * 
 * Centralized axios instance with authentication, CSRF, and error handling
 */
import axios from 'axios';

const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

// Create axios instance
const api = axios.create({
    baseURL: '/api/v1',
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
        'X-Time-Zone': userTimeZone
    }
});

// Request interceptor - add CSRF token and auth
api.interceptors.request.use((config) => {
    // Get CSRF token from hidden form field
    const tokenElement = document.querySelector('input[name=__RequestVerificationToken]');
    if (tokenElement) {
        config.headers['RequestVerificationToken'] = tokenElement.value;
    }
    
    // Transform Date objects to ISO strings without timezone conversion
    if (config.data) {
        transformDates(config.data);
    }
    
    return config;
}, (error) => {
    return Promise.reject(error);
});

// Response interceptor - handle errors
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        // Handle authentication errors
        if (error.response) {
            const status = error.response.status;

            if (status === 401) {
                // Redirect to login for authentication errors (not logged in)
                window.location.href = '/Account/Login';
                return Promise.reject(error);
            }

            if (status === 404) {
                console.error('[API] 404 Not Found:', error.config.url);
            }

            // Handle blob responses (from file download endpoints like audit export)
            // When responseType is 'blob', error response data arrives as a Blob object
            if (error.response.data instanceof Blob) {
                try {
                    const text = await error.response.data.text();
                    error.response.data = JSON.parse(text);
                } catch { /* Not JSON blob - leave as-is */ }
            }

            // Extract error message from response
            // Handles: ApiErrorResponse {message}, SignErrorResponse {error}, and ASP.NET validation {title, errors}
            let errorMessage = error.response.data?.message
                || error.response.data?.error
                || null;

            // Handle ASP.NET / FluentValidation model state errors
            if (!errorMessage && error.response.data?.errors) {
                const firstMessages = Object.values(error.response.data.errors).flat();
                errorMessage = firstMessages.length > 0 ? firstMessages[0] : error.response.data.title;
            }

            errorMessage = errorMessage || error.response.statusText || 'An error occurred';

            error.displayMessage = errorMessage;

            // Expose structured error fields for programmatic handling (3J)
            if (error.response.data) {
                error.errorCode = error.response.data.errorCode;
                error.correlationId = error.response.data.correlationId;
                error.validationErrors = error.response.data.validationErrors;
            }
        } else if (error.request) {
            error.displayMessage = 'Network error - please check your connection';
        } else {
            error.displayMessage = error.message || 'An unexpected error occurred';
        }
        
        return Promise.reject(error);
    }
);

/**
 * Recursively transform Date objects to custom ISO strings
 * This preserves the local date/time without timezone conversion
 */
function transformDates(data) {
    for (const key in data) {
        if (data[key] instanceof Date) {
            data[key] = toCustomISOString(data[key]);
        } else if (typeof data[key] === 'object' && data[key] !== null) {
            transformDates(data[key]);
        }
    }
}

/**
 * Convert Date to ISO string without timezone conversion
 */
function toCustomISOString(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

export default api;
